<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'

import { usePagination } from '../composables/use-pagination'

import type { ComposedPageMeta } from '@xcel-next/composer-schema'

const props = defineProps<{
  pageMeta: ComposedPageMeta,
  useSeparator?: string
}>()

const { paginationItems, addItemStyleClass, setBlockEntriesBasedOnViewport } = usePagination(props.pageMeta.numPages ?? 1, props.pageMeta.page ?? 1)

const isFirstPage = computed(() => props.pageMeta.page === 1)
const isLastPage = computed(() => props.pageMeta.page === props.pageMeta.numPages)
const pageSeparator = computed(() => props.useSeparator || '?')
const paramTeaserFilter = computed(() => props.pageMeta.teaserFilter ? `&filtered=${props.pageMeta.teaserFilter}` : '')

const prevHref = computed(() => {
  const { url, page } = props.pageMeta
  const prev = (page ?? 1) - 1
  if (isFirstPage.value || prev === 1) return url

  const query = new URLSearchParams({ page: String(prev) })
  return `${url}${pageSeparator.value}${query.toString()}${paramTeaserFilter.value}`
})

const nextHref = computed(() => {
  const { url, page } = props.pageMeta
  const next = (page ?? 1) + 1
  if (isLastPage.value) return url

  const query = new URLSearchParams({ page: String(next) })
  return `${url}${pageSeparator.value}${query.toString()}${paramTeaserFilter.value}`
})

watch(() => props.pageMeta, () => {
  setBlockEntriesBasedOnViewport(props.pageMeta.numPages ?? 1, props.pageMeta.page ?? 1)
})

onMounted(() => {
  setBlockEntriesBasedOnViewport(props.pageMeta.numPages ?? 1, props.pageMeta.page ?? 1)
})
</script>

<template>
  <ul class="bx-content bx-pager bx-typo--pager"
      :data-bx-pager-active="pageMeta.page"
      :data-bx-pager-num="pageMeta.numPages">
    <li v-if="!isFirstPage"
        class="bx-pager__item bx-pager__item--prev">
      <a rel="prev"
         :href="prevHref"
         aria-label="vorherige Seite">
        <span class="icon icon-arrow_pager-prev">
          <svg><use href="#arrow_pager-prev" /></svg>
        </span>
      </a>
    </li>

    <li v-for="n in paginationItems"
        :key="n"
        :class="addItemStyleClass(n, props.pageMeta.page ?? 1, props.pageMeta.numPages ?? 1)">
      <div v-if="n === (props.pageMeta.page ?? 1)">
        {{ n }}
      </div>
      <a v-else
         :href="(n <= 1 && pageSeparator === '?') ? props.pageMeta.url : props.pageMeta.url+pageSeparator+'page='+(n)+paramTeaserFilter">
        {{ n }}
      </a>
    </li>

    <li v-if="!isLastPage"
        class="bx-pager__item bx-pager__item--next">
      <a rel="next"
         :href="nextHref"
         aria-label="nächste Seite">
        <span class="icon icon-arrow_pager-next">
          <svg><use href="#arrow_pager-next" /></svg>
        </span>
      </a>
    </li>
  </ul>
</template>
